import React, { useEffect, useState } from "react";
import api, { formatDateTime, formatTL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

// Türkçe etiket eşleme — audit_logs tablosunda saklanan İngilizce/kısa kod
// değerleri kullanıcıya anlaşılır Türkçe olarak gösterilsin.
const ACTION_TR = {
  create: "Oluşturma",
  update: "Güncelleme",
  delete: "Silme",
  pay: "Ödeme Alındı",
  login: "Giriş Yaptı",
  logout: "Çıkış Yaptı",
  giris: "Araç Girişi",
  cikis: "Araç Çıkışı",
  waive_guest: "Misafir Muafiyeti",
  create_expense: "Gider Ekleme",
  delete_expense: "Gider Silme",
  create_manual_entry: "Manuel Araç Girişi",
  bank_transfer: "Bankaya Aktarım",
  reverse_transfer: "Aktarım Geri Alma",
  reverse_orphan_transfer: "Kayıp Aktarım Kurtarma",
  resolve_flat_fee: "Sabit Ücretle Kapatma",
  resolve_manual_entry: "Manuel Girişle Eşleştirme",
  dedupe_plate_subs: "Abonelik Temizliği",
  dismiss: "Yoksay",
  approve_plate: "Plaka Onayı",
  reject_plate: "Plaka Reddi",
  subscribe: "Abonelik Başlattı",
  register: "Kayıt Oldu",
  wallet_topup: "Cüzdana Yükleme",
  wallet_debit: "Cüzdandan Düşüm",
};

const ENTITY_TR = {
  session: { label: "Oturum", color: "default" },
  user: { label: "Kullanıcı", color: "secondary" },
  tariff: { label: "Tarife", color: "outline" },
  access_list: { label: "Beyaz/Kara Liste", color: "outline" },
  plan: { label: "Abonelik Planı", color: "outline" },
  corporate_account: { label: "Kurumsal Hesap", color: "outline" },
  app_settings: { label: "Ayarlar", color: "outline" },
  daily_report: { label: "Günlük Rapor", color: "outline" },
  plate_subscription: { label: "Plaka Aboneliği", color: "outline" },
  subscription_payment: { label: "Abonelik Tahsilatı", color: "outline" },
  bank_transfer: { label: "Banka Aktarımı", color: "outline" },
  expense: { label: "Gider", color: "outline" },
  unmatched_exit: { label: "Eşleşmeyen Çıkış", color: "outline" },
  wallet: { label: "Cüzdan", color: "outline" },
  coupon: { label: "Kupon", color: "outline" },
  plate_verification: { label: "Plaka Doğrulama", color: "outline" },
};

// Detay sütununun bir sözlük döneceği durumlarda kullanıcıya anlamlı çevirileri
// gösterelim. `raw` bir obje veya null; string döner.
function formatDetail(item) {
  const extra = item.extra;
  const after = item.after;
  const before = item.before;
  const entity = item.entity_type;
  const action = item.action;

  // Ödeme — tutar ve yöntem
  if (action === "pay" && extra) {
    const via = { cash: "Nakit", card: "Kart", bank_transfer: "EFT/Havale", wallet: "Cüzdan" }[extra.via] || extra.via;
    return `${formatTL(extra.amount || 0)} • ${via}`;
  }
  // Araç giriş/çıkış
  if ((action === "giris" || action === "cikis") && after) {
    const lot = after.lot === "ust_kat" ? "Üst Kat" : after.lot === "alt_kat" ? "Alt Kat" : (after.lot || "");
    return `${after.plate || "?"} · ${lot}`;
  }
  // Abonelik temizliği
  if (action === "dedupe_plate_subs" && extra) {
    return `${extra.plates_touched} plaka, ${extra.subs_deactivated} kayıt kapatıldı`;
  }
  // Bankaya aktarım
  if (action === "bank_transfer" && after) {
    const via = { cash: "Nakit", card: "Kart" }[after.method] || after.method;
    return `${via} → Banka · ${formatTL(after.amount || 0)} (${after.count || 0} tahsilat)`;
  }
  // Aktarım geri alma
  if (action === "reverse_transfer" && before) {
    const via = { cash: "Nakit", card: "Kart" }[before.method] || before.method;
    return `${via} aktarımı iptal · ${formatTL(before.amount || 0)}`;
  }
  // Kayıp aktarım kurtarma
  if (action === "reverse_orphan_transfer" && extra) {
    return `${formatTL(extra.amount_recovered || 0)} kurtarıldı (${extra.count || 0} kayıt)`;
  }
  // Gider oluşturma
  if (action === "create_expense" && after) {
    return `${after.category || "?"} · ${formatTL(after.amount || 0)} (${after.method || "?"})`;
  }
  // Gider silme
  if (action === "delete_expense" && before) {
    return `${before.category || "?"} · ${formatTL(before.amount || 0)}`;
  }
  // Abonelik tahsilatı
  if (action === "create" && entity === "subscription_payment" && after) {
    const via = { cash: "Nakit", card: "Kart", bank_transfer: "EFT/Havale" }[after.method] || after.method;
    return `${after.plate} · ${formatTL(after.amount || 0)} · ${via}`;
  }
  // Misafir muafiyeti
  if (action === "waive_guest" && after) {
    return `${after.plate || "?"} · muaf (${formatTL(after.discount_amount || 0)} indirim)`;
  }
  // Manuel giriş
  if (action === "create_manual_entry" && after) {
    const lot = after.parking_lot_key === "ust_kat" ? "Üst Kat" : "Alt Kat";
    return `${after.plate} · ${lot}`;
  }
  // Sabit ücretle kapatma
  if (action === "resolve_flat_fee" && extra) {
    return `${extra.plate || "?"} · ${formatTL(extra.amount || 0)}`;
  }
  // Kullanıcı işlemleri
  if (entity === "user") {
    if (action === "create" && after) return `Yeni: ${after.email || "?"} (${after.role || "?"})`;
    if (action === "update" && after) {
      const keys = Object.keys(after).filter(k => k !== "password").join(", ");
      return keys || "Güncelleme";
    }
    if (action === "delete" && before) return `Silinen: ${before.email || "?"} (${before.role || "?"})`;
    if (action === "login") return `Giriş yaptı`;
    if (action === "approve_plate" && after) return `Plaka onayı: ${after.plate || "?"}`;
    if (action === "reject_plate" && after) return `Plaka reddi: ${after.plate || "?"}`;
  }
  // Silme genel
  if (action === "delete" && before) {
    if (before.plate) return `Plaka: ${before.plate}`;
    if (before.name) return `İsim: ${before.name}`;
    if (before.email) return `Email: ${before.email}`;
    if (before.amount != null) return `Tutar: ${formatTL(before.amount)}`;
  }
  // Fallback — raw string kısaltılmış
  const raw = extra || after || before;
  if (!raw) return "—";
  const s = JSON.stringify(raw);
  return s.length > 100 ? s.slice(0, 100) + "…" : s;
}

export default function AuditLog() {
  const { user: authUser } = useAuth();
  const isSuperAdmin = authUser?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [entity, setEntity] = useState("all");

  const load = async () => {
    const params = new URLSearchParams({ limit: "200" });
    if (entity !== "all") params.set("entity_type", entity);
    const { data } = await api.get(`/audit-logs?${params.toString()}`);
    setItems(data);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [entity]);

  const deleteLog = async (it) => {
    if (!window.confirm(
      `Bu denetim kaydını silmek istediğinize emin misiniz?\n\n` +
      `İşlem: ${ACTION_TR[it.action] || it.action}\n` +
      `Nesne: ${(ENTITY_TR[it.entity_type]?.label) || it.entity_type}\n` +
      `Kullanıcı: ${it.actor_email || "—"}\n\n` +
      `NOT: Silme işleminin kendisi de yeni bir denetim kaydı olarak yazılır ` +
      `(kimin sildiği görünür kalır).`
    )) return;
    try {
      await api.delete(`/audit-logs/${it.id}`);
      toast.success("Kayıt silindi (silme işlemi de log'a yazıldı)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  const deleteAllLogs = async () => {
    if (!window.confirm(
      `⚠️ DİKKAT — TÜM DENETİM KAYITLARINI SİLECEKSİNİZ\n\n` +
      `Bu işlem geri alınamaz. Toplam ${items.length}+ kayıt silinecek.\n\n` +
      `Toplu silmenin kendisi tek yeni bir kayıt olarak yazılır ` +
      `(kimin ne zaman sildiği izlenir).\n\n` +
      `Devam etmek için TAMAM'a basın.`
    )) return;
    // İkinci onay
    const answer = window.prompt(
      `Son onay: "SIL" yazıp Enter'a basın.`, ""
    );
    if (answer !== "SIL") { toast.info("İşlem iptal edildi"); return; }
    try {
      const { data } = await api.delete("/audit-logs");
      toast.success(`${data.deleted} kayıt silindi. Yeni denetim izi oluşturuldu.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  const ENTITY_FILTERS = [
    { key: "all", label: "Tümü" },
    { key: "session", label: "Oturum" },
    { key: "user", label: "Kullanıcı" },
    { key: "tariff", label: "Tarife" },
    { key: "access_list", label: "Beyaz/Kara Liste" },
    { key: "plan", label: "Abonelik Planı" },
    { key: "corporate_account", label: "Kurumsal Hesap" },
    { key: "plate_subscription", label: "Plaka Aboneliği" },
    { key: "subscription_payment", label: "Abonelik Tahsilatı" },
    { key: "bank_transfer", label: "Banka Aktarımı" },
    { key: "expense", label: "Gider" },
    { key: "unmatched_exit", label: "Eşleşmeyen Çıkış" },
    { key: "app_settings", label: "Ayarlar" },
    { key: "daily_report", label: "Günlük Rapor" },
    { key: "wallet", label: "Cüzdan" },
    { key: "coupon", label: "Kupon" },
    { key: "plate_verification", label: "Plaka Doğrulama" },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Denetim</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Denetim Kaydı</h1>
          <div className="text-sm text-muted-foreground mt-1">
            Sistemdeki tüm önemli işlemler (oluşturma, güncelleme, silme, ödeme) burada kaydedilir.
          </div>
        </div>
        <div className="flex items-end gap-2">
          {isSuperAdmin && (
            <Button variant="destructive" size="sm" onClick={deleteAllLogs}
                    data-testid="audit-delete-all"
                    className="rounded-sm uppercase tracking-widest text-xs">
              <Trash size={14} /> Tümünü Sil
            </Button>
          )}
          <Select value={entity} onValueChange={setEntity}>
            <SelectTrigger className="w-64 rounded-sm" data-testid="audit-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENTITY_FILTERS.map(f => (
                <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zaman</TableHead>
                <TableHead>Kullanıcı</TableHead>
                <TableHead>İşlem</TableHead>
                <TableHead>Nesne</TableHead>
                <TableHead>Detay</TableHead>
                <TableHead>IP</TableHead>
                {isSuperAdmin && <TableHead className="w-12 text-right">Sil</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 7 : 6} className="text-center py-8 text-muted-foreground">
                    Kayıt yok
                  </TableCell>
                </TableRow>
              )}
              {items.map((it) => {
                const entityInfo = ENTITY_TR[it.entity_type] || { label: it.entity_type, color: "outline" };
                const actionLabel = ACTION_TR[it.action] || it.action;
                return (
                  <TableRow key={it.id} data-testid={`audit-row-${it.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {formatDateTime(it.timestamp)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{it.actor_email || "—"}</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">
                        {it.actor_role === "super_admin" ? "Süperadmin"
                         : it.actor_role === "admin" ? "Yönetici"
                         : it.actor_role === "operator" ? "Operatör"
                         : it.actor_role === "employee" ? "Çalışan"
                         : it.actor_role === "customer" ? "Müşteri"
                         : it.actor_role === "system" ? "Sistem"
                         : (it.actor_role || "—")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="rounded-sm text-[11px]" data-testid={`audit-action-${it.id}`}>
                        {actionLabel}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={entityInfo.color} className="rounded-sm text-[11px]" data-testid={`audit-entity-${it.id}`}>
                        {entityInfo.label}
                      </Badge>
                      {it.entity_id && (
                        <div className="text-[10px] text-muted-foreground font-mono mt-1">
                          {it.entity_id.slice(-8)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs max-w-xs" data-testid={`audit-detail-${it.id}`}>
                      <div className="line-clamp-2">{formatDetail(it)}</div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {it.ip || "—"}
                    </TableCell>
                    {isSuperAdmin && (
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => deleteLog(it)}
                                data-testid={`audit-delete-${it.id}`}
                                title="Süperadmin: Kaydı sil (silme işlemi de audit'e yazılır)"
                                className="h-8 w-8 p-0 text-destructive">
                          <Trash size={13} />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
